import React, { ReactElement } from 'react';
import { useMutation } from '@apollo/client';

import { getClientEnvironmentDetails } from '@sm/utils';

import { errorHandler as clientErrorHandler } from '../../../helpers/errorHandler';
import SaveTreatmentMutation from './SaveTreatment.graphql';
import {
  ExperimentAssignmentType,
  SaveExperimentTreatmentPayload,
} from '../../../types';
import { TreatmentItem } from '..';

/**
 * If the user is LO and we have values from the Svc,
 * we can set a cookie for experimentsvc to injest
 * @param {Object} saveTreatmentPayload
 * @return {null}
 */
const createCookie = (
  saveTreatmentPayload: SaveExperimentTreatmentPayload
): null => {
  const { isBrowser } = getClientEnvironmentDetails();
  // no need to create a cookie if there isn't a cookie to create
  if (saveTreatmentPayload.success === false || !saveTreatmentPayload.name) {
    return null;
  }
  const { name, maxAge, assignments } = saveTreatmentPayload;
  if (isBrowser) {
    const { host } = window.location;
    const domain = host.replace(/^[^.]+\./g, '');
    document.cookie = `${name}=${assignments}; max-age=${maxAge}; path=/; domain=${domain}`;
  }
  return null;
};

const EXPERIMENT_ASSIGNMENT_TYPE_TO_SCHEMA = Object.freeze({
  NEW: ExperimentAssignmentType.New,
  EXISTING: ExperimentAssignmentType.Existing,
  WINNER: ExperimentAssignmentType.Winner,
} as const) as Record<string, ExperimentAssignmentType>;

type TreatmentProps = {
  when: string;
  treatments: Record<string, TreatmentItem>;
  control?: boolean;
  children: React.ReactNode;
};

/**
 * Component to display a treatment
 * @param {String} when
 * @param {Object} treatments
 * @param {Boolean} control
 * @param {Function|Object} children
 * @returns {*}
 */
const Treatment = ({
  when,
  treatments,
  control = false,
  children,
}: TreatmentProps): ReactElement => {
  const [saveTreatment] = useMutation(SaveTreatmentMutation);
  const childElement = typeof children === 'function' ? children() : children;
  const allTreatments = Object.keys(treatments);

  // If experiments fails, or the user hasn't been assigned and experiment show the control.
  if (allTreatments.length === 0 && control) {
    return childElement;
  }

  // if our treatment exists in the object
  if (allTreatments.length) {
    if (treatments[when] && treatments[when].treatmentName) {
      // send the data back to experiments that we are showing this treatment
      const {
        treatmentId,
        experimentId,
        treatmentName,
        experimentName,
        assignmentType: assignmentTypeString,
      } = treatments[when];
      const assignmentType =
        EXPERIMENT_ASSIGNMENT_TYPE_TO_SCHEMA[assignmentTypeString];
      saveTreatment({
        variables: {
          input: {
            treatmentId,
            treatmentName,
            experimentId,
            experimentName,
            assignmentType,
          },
        },
      })
        .then(({ data }) => {
          if (data && data.saveExperimentTreatment)
            createCookie(data.saveExperimentTreatment);
        })
        .catch(e =>
          // TODO: Remove this cast when clientErrorHandler has been typed
          (clientErrorHandler as any).logError(
            e,
            'Unable to save experiment treatment'
          )
        );
      // Let's return the child element without waiting for the mutation as the
      // mutation doesn't really impact the children.
      return childElement;
    }
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <></>;
};

export default Treatment;
